import React, { useState } from "react";
import { 
  Search, ChevronDown, ChevronUp, HelpCircle, 
  FileText, Percent, Clock, Shield, 
  DollarSign, CreditCard, Calculator, 
  CheckCircle, Sparkles, MessageSquare,
  BookOpen, TrendingUp, Users, Award
} from 'lucide-react';
import { useNavigate } from "react-router-dom";

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  const toggle = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const navigate = useNavigate();

  const faqData = [
    {
      question: "How do I apply for a loan?",
      answer: "You can apply for a loan in three simple steps: 1) Register for an account on our platform, 2) Complete your profile verification, 3) Navigate to the 'Apply Loan' section and fill out the application form. Our AI-powered system will review your application and provide an instant preliminary decision.",
      category: "loans",
      icon: FileText
    },
    {
      question: "What are the current interest rates?",
      answer: "Interest rates vary between 8.5% to 15% APR depending on the loan product, loan term, and your credit profile. Personal loans typically range from 10-12%, business loans from 8.5-11%, and education loans from 9-13%. You'll see the exact rate offered during your application process.",
      category: "rates",
      icon: Percent
    },
    {
      question: "How long does loan approval take?",
      answer: "Most applications receive an instant preliminary decision. Full approval and disbursement typically take 24-48 business hours for standard applications. For larger loan amounts or complex cases, additional verification may extend this to 3-5 business days. We'll keep you updated throughout the process.",
      category: "process",
      icon: Clock
    },
    {
      question: "Is my personal and financial data secure?",
      answer: "Absolutely. We use bank-level 256-bit SSL encryption and comply with international data protection standards (GDPR, CCPA). All sensitive information is encrypted both in transit and at rest. We never share your personal data with third parties without your explicit consent.",
      category: "security",
      icon: Shield
    },
    {
      question: "What are the minimum and maximum loan amounts?",
      answer: "We offer loans ranging from ₨ 25,000 to ₨ 10,000,000 depending on the loan product and your eligibility. New customers typically qualify for amounts between ₨ 25,000 - ₨ 500,000, while established customers with good repayment history can access higher amounts.",
      category: "loans",
      icon: DollarSign
    },
    {
      question: "Can I repay my loan early without penalties?",
      answer: "Yes, early repayment is encouraged and penalty-free. You can make partial or full early payments at any time through your dashboard. Early repayment may significantly reduce your total interest costs. Contact our support team if you need assistance with early settlement calculations.",
      category: "repayment",
      icon: CreditCard
    },
    {
      question: "What documents do I need to apply?",
      answer: "Standard documentation includes: 1) Valid NIC or passport, 2) Recent utility bill (for address verification), 3) Last 3 months' bank statements, 4) Proof of income (salary slips or business financials). Specific requirements may vary by loan type and amount.",
      category: "process",
      icon: FileText
    },
    {
      question: "How is the EMI calculated?",
      answer: "EMI is calculated using the reducing balance method: EMI = [P x R x (1+R)^N]/[(1+R)^N-1], where P = Principal, R = Monthly Interest Rate, N = Loan Tenure in months. Our loan calculator provides detailed breakdowns including principal, interest, and total payment amounts.",
      category: "rates",
      icon: Calculator
    },
    {
      question: "What happens if I miss an EMI payment?",
      answer: "We understand financial difficulties happen. Please contact us immediately if you anticipate payment issues. We offer a 5-day grace period with a nominal late fee of 2% per month. After 30 days, your account may be reported to credit bureaus. We encourage proactive communication to arrange payment plans.",
      category: "repayment",
      icon: Clock
    },
    {
      question: "Can I track my application status in real-time?",
      answer: "Yes, you can track every stage of your application through your dashboard. You'll receive email and SMS notifications for key milestones: application received, under review, approved, funds disbursed. Our 24/7 support team can also provide status updates.",
      category: "process",
      icon: TrendingUp
    }
  ];

  const categories = [
    { id: "all", name: "All Questions", icon: HelpCircle, count: faqData.length },
    { id: "loans", name: "Loan Types", icon: DollarSign, count: faqData.filter(f => f.category === "loans").length },
    { id: "rates", name: "Interest & Fees", icon: Percent, count: faqData.filter(f => f.category === "rates").length },
    { id: "process", name: "Application Process", icon: Clock, count: faqData.filter(f => f.category === "process").length },
    { id: "repayment", name: "Repayment", icon: CreditCard, count: faqData.filter(f => f.category === "repayment").length },
    { id: "security", name: "Security & Privacy", icon: Shield, count: faqData.filter(f => f.category === "security").length },
  ];

  const filteredFaqs = faqData.filter(item => {
    const matchesSearch = item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === "all" || item.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-700 text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6">
              <HelpCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Knowledge Base</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Frequently Asked <span className="text-cyan-300">Questions</span>
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Find answers to common questions about loans, applications, and managing your account.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 -mt-8">
        {/* Search Bar */}
        <div className="bg-white rounded-3xl border border-gray-200 p-8 shadow-xl mb-12">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 mb-8">
            <div className="flex-1 w-full">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search questions... (e.g., 'interest rate', 'documents required')"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-gray-200 text-gray-900 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all"
                />
              </div>
              <p className="text-sm text-gray-500 mt-2 ml-1">
                {searchQuery ? `Found ${filteredFaqs.length} matching questions` : "Type to search through all questions"}
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <MessageSquare className="w-4 h-4" />
                <span>Still have questions?</span>
              </div>
              <button onClick={()=>navigate("/login")} className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl">
                Contact Support
              </button>
            </div>
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-3">
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl transition-all duration-300 ${
                    isActive
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium">{cat.name}</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    isActive ? 'bg-white/20' : 'bg-gray-300'
                  }`}>
                    {cat.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-3xl font-bold text-gray-900">98%</div>
            </div>
            <p className="text-gray-600">Approval Rate</p>
          </div>
          
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="text-3xl font-bold text-gray-900">24h</div>
            </div>
            <p className="text-gray-600">Average Processing</p>
          </div>
          
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-amber-600" />
              </div>
              <div className="text-3xl font-bold text-gray-900">5,000+</div>
            </div>
            <p className="text-gray-600">Happy Customers</p>
          </div>
          
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <Award className="w-5 h-5 text-purple-600" />
              </div>
              <div className="text-3xl font-bold text-gray-900">4.9★</div>
            </div>
            <p className="text-gray-600">Customer Rating</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main FAQ Section */}
          <div className="lg:col-span-2">
            <div className="space-y-4">
              {filteredFaqs.length > 0 ? (
                filteredFaqs.map((item, index) => {
                  const Icon = item.icon;
                  const isActive = activeIndex === index;
                  
                  return (
                    <div
                      key={index}
                      className={`bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden transition-all duration-300 ${
                        isActive ? 'ring-2 ring-blue-500' : ''
                      }`}
                    >
                      <button
                        onClick={() => toggle(index)}
                        className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="text-left">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                              {item.question}
                            </h3>
                            <div className="flex items-center gap-2">
                              <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                                {item.category}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="ml-4 flex-shrink-0">
                          {isActive ? (
                            <ChevronUp className="w-5 h-5 text-blue-600" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </button>

                      <div
                        className={`overflow-hidden transition-all duration-300 ${
                          isActive ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                        }`}
                      >
                        <div className="px-6 pb-6">
                          <div className="pl-14">
                            <div className="prose prose-blue max-w-none">
                              <p className="text-gray-700">{item.answer}</p>
                            </div>
                            {isActive && (
                              <div className="mt-6 pt-6 border-t border-gray-100 flex items-center gap-4">
                                <span className="text-sm text-gray-500">Was this helpful?</span>
                                <div className="flex gap-2">
                                  <button className="px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium">
                                    Yes
                                  </button>
                                  <button className="px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium">
                                    No
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Search className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">No results found</h3>
                  <p className="text-gray-600 max-w-md mx-auto">
                    We couldn't find any questions matching "{searchQuery}". Try different keywords or browse our categories.
                  </p>
                  <button 
                    onClick={() => { setSearchQuery(""); setActiveCategory("all"); }}
                    className="mt-6 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300"
                  >
                    View All Questions
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Quick Help */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl border border-blue-100 p-8 shadow-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                  <HelpCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Need More Help?</h3>
                  <p className="text-gray-600">Our team is ready to assist you</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="p-4 bg-white rounded-xl border border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-2">Live Chat</h4>
                  <p className="text-sm text-gray-600 mb-3">Chat with our support team in real-time</p>
                  <button onClick ={()=>navigate("/login")} className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 text-sm font-medium">
                    Start Chat
                  </button>
                </div>
                
                <div className="p-4 bg-white rounded-xl border border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-2">Email Support</h4>
                  <p className="text-sm text-gray-600 mb-3">Get detailed responses within 4 hours</p>
                  <a 
                    href="mailto:support@smartlend.com"
                    className="block text-center px-4 py-2 bg-white text-blue-600 rounded-lg border border-blue-300 hover:bg-blue-50 transition-colors text-sm font-medium"
                  >
                    Email Us
                  </a>
                </div>
                
                <div className="p-4 bg-white rounded-xl border border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-2">Phone Support</h4>
                  <p className="text-sm text-gray-600 mb-3">Call us for immediate assistance</p>
                  <a 
                    href="tel:+94771234567"
                    className="block text-center px-4 py-2 bg-white text-blue-600 rounded-lg border border-blue-300 hover:bg-blue-50 transition-colors text-sm font-medium"
                  >
                    +94 77 123 4567
                  </a>
                </div>
              </div>
            </div>

            {/* Popular Resources */}
            <div className="bg-white rounded-3xl border border-gray-200 p-8 shadow-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-green-500 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Popular Resources</h3>
                  <p className="text-gray-600">Helpful guides and articles</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <a href="#" className="group block p-4 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors border border-gray-200 hover:border-blue-300">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                      <FileText className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 group-hover:text-blue-600">Loan Application Guide</h4>
                      <p className="text-xs text-gray-500">Step-by-step instructions</p>
                    </div>
                  </div>
                </a>
                
                <a href="#" className="group block p-4 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors border border-gray-200 hover:border-blue-300">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                      <Calculator className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 group-hover:text-emerald-600">EMI Calculator Guide</h4>
                      <p className="text-xs text-gray-500">Understand your payments</p>
                    </div>
                  </div>
                </a>
                
                <a href="#" className="group block p-4 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors border border-gray-200 hover:border-blue-300">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                      <Shield className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 group-hover:text-amber-600">Security Best Practices</h4>
                      <p className="text-xs text-gray-500">Protect your account</p>
                    </div>
                  </div>
                </a>
              </div>
            </div>

            {/* Quick Tip */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl border border-amber-100 p-8 shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Pro Tip</h3>
                  <p className="text-sm text-gray-600">Increase your approval chances</p>
                </div>
              </div>
              
              <p className="text-gray-700 mb-4">
                Complete your profile with accurate information and upload clear documents. Applications with complete profiles are processed 40% faster.
              </p>
              
              <a 
                href="/profile"
                className="inline-flex items-center gap-2 text-amber-600 font-semibold hover:text-amber-700 transition-colors"
              >
                Update Profile
                <ChevronDown className="w-4 h-4 rotate-270" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Still Have Questions?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Our support team is available 24/7 to help with any questions or concerns.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-4 bg-white text-blue-700 font-semibold rounded-xl hover:bg-blue-50 transition-all duration-300 shadow-2xl hover:shadow-3xl">
              Contact Support Now
            </button>
            
            <button className="px-8 py-4 bg-transparent text-white font-medium rounded-xl border-2 border-white/30 hover:bg-white/10 transition-all duration-300">
              Schedule a Call
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;